import { type FC, useEffect, useState } from "react";
import { Button } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { ConfirmationModal } from "@packsize/components";
import { useToast } from "@packsize/toast";
import { useToken, useUser } from "@packsize/authentication";

import { type ExternalAccessType } from "@/schemas";
import { useCreateExternalAccessSubscription } from "@/hooks/use-external-access-subscriptions";

import {
	type ApiKeyResponse,
	type OAuthResponse,
	type ProductItem,
	type WizardData,
	type WizardStep,
} from "../../types";

import { ConnectionConfigStep } from "./steps/connection-config-step";
import { ApiKeySuccessStep } from "./steps/api-key-success-step";
import { OAuthSuccessStep } from "./steps/oauth-success-step";
import { convertOauthScopes, useProductList } from "./product-list";

export const AddApiConnection: FC = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [currentStep, setCurrentStep] = useState<WizardStep>("config");
	const [wizardData, setWizardData] = useState<Partial<WizardData>>({ connectionType: "apiKey" });
	const [products, setProducts] = useState<ProductItem[]>([]);
	const [hasNameError, setHasNameError] = useState(false);
	const [hasProductError, setHasProductError] = useState(false);
	const [hasTypeError, setHasTypeError] = useState(false);
	const [generatedCredentials, setGeneratedCredentials] = useState<ApiKeyResponse>({});
	const [generatedOAuthCredentials, setGeneratedOAuthCredentials] = useState<OAuthResponse>({});

	const intl = useIntl();
	const { toastSuccess } = useToast();
	const { hasLicense } = useUser();
	const { decodedToken } = useToken();

	const createMutation = useCreateExternalAccessSubscription(decodedToken?.tid);

	const productList = useProductList(hasLicense);

	useEffect(() => {
		const filteredProductsList = productList.filter((product) => product.hasLicense());
		setProducts(filteredProductsList);
	}, [decodedToken]);

	const resetWizard = () => {
		setCurrentStep("config");
		setWizardData({
			connectionType: "apiKey",
		});
		setHasNameError(false);
		setHasProductError(false);
		setHasTypeError(false);
		setGeneratedCredentials({});
		setGeneratedOAuthCredentials({});
		setProducts(productList.filter((product) => product.hasLicense()));
	};

	const handleOpen = () => {
		setIsOpen(true);
		resetWizard();
	};

	const handleClose = () => {
		setIsOpen(false);
		resetWizard();
	};

	const handleConnectionTypeChange = (type: ExternalAccessType) => {
		setWizardData({ ...wizardData, connectionType: type });
		setHasTypeError(false);
	};

	const handleNameChange = (name: string) => {
		setWizardData({ ...wizardData, name });

		if (hasNameError && name.trim()) {
			setHasNameError(false);
		}
	};

	const handleProductChange = (productName: string, checked: boolean) => {
		const updatedProducts = products.map((product) =>
			product.name === productName ? { ...product, value: checked } : product,
		);
		setProducts(updatedProducts);

		const selectedProducts = updatedProducts.filter((p) => p.value).map((p) => p.mappedValue);
		setWizardData({ ...wizardData, selectedProducts });

		if (hasProductError && selectedProducts.length > 0) {
			setHasProductError(false);
		}
	};

	const validateCurrentStep = (): boolean => {
		if (currentStep === "config") {
			let hasError = false;

			if (!wizardData.connectionType) {
				setHasTypeError(true);
				hasError = true;
			}

			if (!wizardData.name?.trim()) {
				setHasNameError(true);
				hasError = true;
			}

			if (!wizardData.selectedProducts || wizardData.selectedProducts.length === 0) {
				setHasProductError(true);
				hasError = true;
			}

			return !hasError;
		}

		return true;
	};

	const handleGenerate = async () => {
		if (!wizardData.name || !wizardData.selectedProducts || !wizardData.connectionType) {
			return;
		}

		if (!decodedToken) {
			return;
		}

		const selectedProducts =
			wizardData.connectionType === "oauth"
				? convertOauthScopes(wizardData.selectedProducts)
				: wizardData.selectedProducts;

		const result = await createMutation.mutateAsync({
			type: wizardData.connectionType,
			displayName: wizardData.name,
			selectedProducts,
		});

		if (wizardData.connectionType === "oauth") {
			setGeneratedOAuthCredentials(result as OAuthResponse);
			setCurrentStep("oauthSuccess");
		} else {
			setGeneratedCredentials(result as ApiKeyResponse);
			setCurrentStep("apiKeySuccess");
		}
	};

	const handleNext = async () => {
		if (!validateCurrentStep()) {
			return;
		}

		await handleGenerate();
	};

	const copyToClipboard = async (value: string): Promise<void> => {
		await navigator.clipboard.writeText(value);
		toastSuccess({ message: intl.formatMessage({ id: "Copied to clipboard", defaultMessage: "Copied to clipboard" }) });
	};

	const renderStepContent = () => {
		switch (currentStep) {
			case "config":
				return (
					<ConnectionConfigStep
						connectionType={wizardData.connectionType}
						onConnectionTypeChange={handleConnectionTypeChange}
						name={wizardData.name || ""}
						onNameChange={handleNameChange}
						products={products}
						onProductChange={handleProductChange}
						hasNameError={hasNameError}
						hasProductError={hasProductError}
						hasTypeError={hasTypeError}
					/>
				);

			case "apiKeySuccess":
				return <ApiKeySuccessStep credentials={generatedCredentials} onCopyToClipboard={copyToClipboard} />;

			case "oauthSuccess":
				return <OAuthSuccessStep credentials={generatedOAuthCredentials} onCopyToClipboard={copyToClipboard} />;

			default:
				return null;
		}
	};

	const modalTitle =
		currentStep === "apiKeySuccess" || currentStep === "oauthSuccess"
			? intl.formatMessage({ id: "Connection Created", defaultMessage: "Connection Created" })
			: intl.formatMessage({ id: "Add API Connection", defaultMessage: "Add API Connection" });

	const isSuccessStep = currentStep === "apiKeySuccess" || currentStep === "oauthSuccess";

	return (
		<>
			<Button variant="contained" onClick={handleOpen} data-pendoid="add-api-connection__button__open-wizard">
				<FormattedMessage id="Add API Connection" defaultMessage="Add API Connection" />
			</Button>

			<ConfirmationModal
				open={isOpen}
				onClose={(_) => {
					if (createMutation.isPending === false) {
						handleClose();
					}
				}}
				title={modalTitle}
				confirmText={intl.formatMessage({ id: "Add API Connection", defaultMessage: "Add API Connection" })}
				cancelText={
					isSuccessStep
						? intl.formatMessage({ id: "Close", defaultMessage: "Close" })
						: intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })
				}
				onConfirm={handleNext}
				onCancel={handleClose}
				size="lg"
				slotProps={{
					confirmButton: isSuccessStep
						? { sx: { display: "none" } }
						: {
								loading: createMutation.isPending,
								"data-pendoid": "add-api-connection__button__generate",
							},
					cancelButton: {
						disabled: createMutation.isPending,
						"data-pendoid": isSuccessStep ? undefined : "add-api-connection__button__cancel",
						variant: isSuccessStep ? "contained" : "text",
					},
				}}
			>
				{renderStepContent()}
			</ConfirmationModal>
		</>
	);
};
